import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import { markHydrated } from './hooks/useHydrated';
import { preloadHydratedChunks } from './hydrationPreload';
const AmazonReturnsPage = lazy(() => import('./pages/AmazonReturnsPage'));
const AmazonWarrantyPage = lazy(() => import('./pages/AmazonWarrantyPage'));
const StopTrackingPage = lazy(() => import('./pages/StopTrackingPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AmazonSellersPage = lazy(() => import('./pages/AmazonSellersPage'));
const AmazonPrimeDayPage = lazy(() => import('./pages/AmazonPrimeDayPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const MyTrackingPage = lazy(() => import('./pages/MyTrackingPage'));
const AliexpressCouponsPage = lazy(() => import('./pages/AliexpressCouponsPage'));
const AmazonFreeShippingPage = lazy(() => import('./pages/AmazonFreeShippingPage'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const MelittaSoloPage = lazy(() => import('./pages/info/MelittaSoloPage'));
const SageBaristaExpressPage = lazy(() => import('./pages/info/SageBaristaExpressPage'));
import StatsWrapper from './utils/StatsWrapper';
import Layout from './components/Layout';
import AdminRoute from './components/AdminRoute';
const AdminItemsPage = lazy(() => import('./pages/AdminItemsPage'));
const AdminFlagsPage = lazy(() => import('./pages/AdminFlagsPage'));

// Page tracking component
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Get page name from path
    const getPageName = (pathname) => {
      switch (pathname) {
        case '/':
          return 'Home-Page';
        case '/stop-tracking':
          return 'Stop-Tracking-Page';
        case '/terms':
          return 'Terms-and-Conditions-Page';
        case '/about':
          return 'About-Page';
        case '/amazon-returns':
          return 'Amazon-Returns-Page';
        case '/amazon-warranty':
          return 'Amazon-Warranty-Page';
        case '/amazon-sellers':
          return 'Amazon-Sellers-Page';
        case '/amazon-prime-day':
          return 'Amazon-Prime-Day-Page';
        case '/guide':
          return 'Guide-Page';
        case '/contact':
          return 'Contact-Page';
        case '/my-tracking':
          return 'My-Tracking-Page';
        case '/aliexpress-coupons':
          return 'Aliexpress-Coupons-Page';
        case '/info':
          return 'Info-Hub-Page';
        case '/info/melitta-solo':
          return 'Melitta-Solo-Page';
        case '/info/sage-barista-express':
          return 'Sage-Barista-Express-Page';
        case '/amazon-free-shipping':
          return 'Amazon-Free-Shipping-Page';
        default:
          return 'Unknown-Page';
      }
    };

    const pageName = getPageName(location.pathname);
    StatsWrapper.track('page.view', { page: pageName });
  }, [location]);

  return null;
}

export default function App() {
  // Flips the single shared useHydrated() signal (src/hooks/useHydrated.js) once,
  // here at the true root of the client tree, only after every hydrated-gated lazy
  // chunk (src/hydrationPreload.js) has finished loading. On a /search/{slug} page a
  // bare flip made every one of those ~40 Suspense boundaries (one ItemFullscreenModal
  // per product card, plus every other hydrated-gated modal/banner) suspend and
  // resolve their import() in the very same commit - production stack traces showed
  // React's Suspense retry bookkeeping corrupted by that many simultaneous first-time
  // resolutions (React #418/#423/#321). Preloading first means every boundary's
  // import() is already resolved by the time it tries to mount, so none of them
  // suspend at all.
  useEffect(() => {
    let cancelled = false;
    let rafId = null;
    preloadHydratedChunks().then(() => {
      // Deliberately not calling markHydrated() directly off this promise
      // continuation: that runs as a microtask, which can land in the middle of
      // React's own microtask-queued work for the commit that just finished (effect
      // flushing, etc.). requestAnimationFrame defers to a fresh macrotask, after the
      // browser has painted and React has fully settled - so the resulting mass-mount
      // of every hydrated-gated component starts from a clean slate instead of
      // possibly overlapping React's own in-flight bookkeeping.
      if (cancelled) return;
      rafId = requestAnimationFrame(() => {
        if (!cancelled) markHydrated();
      });
    });
    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <PageViewTracker />
      <Routes>
        <Route
          path="/"
          element={
            <Layout titleVariant="hero">
              <HomePage />
            </Layout>
          }
        />
        <Route
          path="/stop-tracking"
          element={
            <Layout>
              <Suspense fallback={<div>הפסק מעקב</div>}>
                <StopTrackingPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/terms"
          element={
            <Layout>
              <Suspense fallback={<div>תנאי שימוש והגבלות</div>}>
                <TermsAndConditionsPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/about"
          element={
            <Layout>
              <Suspense fallback={<div>אודותינו</div>}>
                <AboutPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/amazon-returns"
          element={
            <Layout>
              <Suspense fallback={<div>החזרות לאמזון - מדריך מקיף</div>}>
                <AmazonReturnsPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/amazon-warranty"
          element={
            <Layout>
              <Suspense fallback={<div>אחריות מוצרים באמזון - מדריך מקיף</div>}>
                <AmazonWarrantyPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/amazon-sellers"
          element={
            <Layout>
              <Suspense fallback={<div>הבנת מוכרים באמזון</div>}>
                <AmazonSellersPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/amazon-prime-day"
          element={
            <Layout>
              <Suspense fallback={<div>Amazon Prime Day 2025</div>}>
                <AmazonPrimeDayPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/amazon-free-shipping"
          element={
            <Layout>
              <Suspense fallback={<div>משלוח חינם מאמזון לישראל</div>}>
                <AmazonFreeShippingPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/guide"
          element={
            <Layout>
              <Suspense fallback={<div>מדריכים ומאמרים</div>}>
                <GuidePage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/contact"
          element={
            <Layout>
              <Suspense fallback={<div>צור קשר</div>}>
                <ContactPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/my-tracking"
          element={
            <Layout>
              <Suspense fallback={<div>המעקבים שלי</div>}>
                <MyTrackingPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/aliexpress-coupons"
          element={
            <Layout>
              <Suspense fallback={<div>קופונים לעליאקספרס</div>}>
                <AliexpressCouponsPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/info/melitta-solo"
          element={
            <Layout>
              <Suspense fallback={<div>טוען...</div>}>
                <MelittaSoloPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/info"
          element={
            <Layout>
              <Suspense fallback={<div>מרכז המידע...</div>}>
                <InfoPage />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/info/sage-barista-express"
          element={
            <Layout>
              <Suspense fallback={<div>טוען...</div>}>
                <SageBaristaExpressPage />
              </Suspense>
            </Layout>
          }
        />
        {/* SearchPage renders SearchResultsPage, which already includes its own Layout
            (shared with entry-server.jsx, see SearchResultsPage.jsx) - no outer Layout
            wrapper here, or hydration would see two nested Layouts. */}
        <Route path="/search/:slug" element={<SearchPage />} />
        <Route path="/admin/items" element={<AdminRoute Page={AdminItemsPage} />} />
        <Route path="/admin/flags" element={<AdminRoute Page={AdminFlagsPage} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
